import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useStore } from 'context/store-context';
import { useUser } from 'context/user-context';
import { GET_APP_EMBED_STATUS, GET_PROTECTION_SETTINGS } from 'gql/queries';
import { userRoles } from 'types/user';
import { isShipaidAdmin } from 'utils';
import { FeatureLanding } from './FeatureLanding';
import { detectFeatureState } from './featureStatus';

type FocusRefresh = {
  storeId: string;
  featureId: string;
  pending: boolean;
  failed: boolean;
};

export function FeatureLandingRoute() {
  const { featureId = '' } = useParams();
  const { storeId, storeProperties, storeLoading, refetchStoreProperties } =
    useStore();
  const { role, user } = useUser();
  const checkGuarantee = featureId === 'delivery-guarantee' && Boolean(storeId);
  const protection = useQuery(GET_PROTECTION_SETTINGS, {
    variables: { storeId },
    skip: !checkGuarantee,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const embed = useQuery(GET_APP_EMBED_STATUS, {
    variables: { storeId },
    skip: !checkGuarantee,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const [focusRefresh, setFocusRefresh] = useState<FocusRefresh | null>(null);
  const refreshGeneration = useRef(0);
  const refreshTargets = useRef({
    store: refetchStoreProperties,
    protection: protection.refetch,
    embed: embed.refetch,
  });
  useLayoutEffect(() => {
    refreshTargets.current = {
      store: refetchStoreProperties,
      protection: protection.refetch,
      embed: embed.refetch,
    };
  }, [refetchStoreProperties, protection.refetch, embed.refetch]);

  useEffect(() => {
    let active = true;
    const refresh = () => {
      if (!storeId) return;
      const generation = ++refreshGeneration.current;
      const targets = refreshTargets.current;
      setFocusRefresh({ storeId, featureId, pending: true, failed: false });
      const requests: Promise<unknown>[] = [
        Promise.resolve().then(() => targets.store?.()),
      ];
      if (checkGuarantee) {
        requests.push(
          Promise.resolve().then(() => targets.protection({ storeId })),
          Promise.resolve().then(() => targets.embed({ storeId })),
        );
      }
      Promise.allSettled(requests).then((results) => {
        if (!active || generation !== refreshGeneration.current) return;
        setFocusRefresh({
          storeId,
          featureId,
          pending: false,
          failed: results.some((result) => result.status === 'rejected'),
        });
      });
    };
    window.addEventListener('focus', refresh);
    return () => {
      active = false;
      window.removeEventListener('focus', refresh);
    };
  }, [storeId, featureId, checkGuarantee]);

  const currentRefresh =
    focusRefresh &&
    focusRefresh.storeId === storeId &&
    focusRefresh.featureId === featureId
      ? focusRefresh
      : null;
  const settings = protection.data?.protectionSettings?.find(
    (setting: { storeId: string }) => setting.storeId === storeId,
  );
  const embedValue = embed.data?.getAppEmbedStatus?.['shipaid-widget'];
  const enabled =
    embedValue === true || embedValue === 'true' || embedValue === 1;
  const disabled =
    embedValue === false || embedValue === 'false' || embedValue === 0;
  const state = currentRefresh?.failed
    ? 'unknown'
    : detectFeatureState(featureId, {
        storeId,
        store: storeProperties,
        loading: storeLoading || currentRefresh?.pending,
        guarantee: checkGuarantee
          ? {
              loading:
                protection.loading ||
                embed.loading ||
                protection.variables?.storeId !== storeId ||
                embed.variables?.storeId !== storeId,
              unavailable: Boolean(
                protection.error ||
                  embed.error ||
                  !Array.isArray(protection.data?.protectionSettings) ||
                  (!enabled && !disabled),
              ),
              feeConfigured: Boolean(
                settings &&
                  (settings.defaultFee != null || settings.percentage != null),
              ),
              embedEnabled: enabled,
            }
          : undefined,
      });
  return (
    <FeatureLanding
      featureId={featureId}
      state={state}
      canConfigure={
        role === userRoles.ADMIN ||
        role === userRoles.MANAGER ||
        isShipaidAdmin(user)
      }
    />
  );
}
